import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 12 production languages in the same order as the audio pipeline processes them.
const LANG_LIST = [
  ["en", "🇬🇧 English"],
  ["tr", "🇹🇷 Turkish"],
  ["de", "🇩🇪 German"],
  ["fr", "🇫🇷 French"],
  ["es", "🇪🇸 Spanish"],
  ["it", "🇮🇹 Italian"],
  ["nl", "🇳🇱 Dutch"],
  ["el", "🇬🇷 Greek"],
  ["ru", "🇷🇺 Russian"],
  ["ar", "🇸🇦 Arabic"],
  ["zh", "🇨🇳 Chinese"],
  ["hi", "🇮🇳 Hindi"],
];

export default function AudioWorkerPanel({ secret }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await fetch(`${API}/admin/${secret}/worker/status`);
      if (!r.ok) throw new Error("Failed to fetch worker status");
      setStatus(await r.json());
      setErr("");
    } catch (e) {
      setErr(e.message || "Failed");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [secret]);

  const action = async (kind) => {
    setBusy(true);
    try {
      await fetch(`${API}/admin/${secret}/worker/${kind}`, { method: "POST" });
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return <div className="admin-muted" data-testid="worker-loading">Loading audio worker status…</div>;
  }

  const audio = status.audio_in_storage || {};
  const translations = status.translations || {};
  const totalDone = LANG_LIST.reduce((s, [c]) => s + Math.min(audio[c] || 0, 56), 0);
  const totalCap = LANG_LIST.length * 56;
  const pct = Math.round((100 * totalDone) / totalCap);

  return (
    <div data-testid="worker-panel">
      {err && <div className="admin-err">{err}</div>}

      {/* Live status header */}
      <div className="worker-headline">
        <div className="worker-statedot" data-running={status.running ? "1" : "0"} />
        <div className="worker-statetext">
          {status.running
            ? <>Worker <strong>running</strong> — currently producing <strong>{status.current_lang || "—"}</strong></>
            : <>Worker <strong>idle</strong></>}
        </div>
        <div className="worker-actions">
          <button
            className="admin-btn"
            disabled={busy || status.running}
            onClick={() => action("start")}
            data-testid="worker-start"
          >
            Start
          </button>
          <button
            className="admin-btn"
            disabled={busy || !status.running}
            onClick={() => action("stop")}
            data-testid="worker-stop"
          >
            Stop
          </button>
          <button
            className="admin-btn admin-btn-ghost"
            onClick={load}
            disabled={busy}
            data-testid="worker-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Overall progress bar */}
      <div className="worker-progress">
        <div className="worker-progress-bar" style={{ width: `${pct}%` }} />
        <span className="worker-progress-text">{totalDone} / {totalCap} chapters across 12 languages ({pct}%)</span>
      </div>

      {/* Per-language table */}
      <table className="worker-table">
        <thead>
          <tr>
            <th>Language</th>
            <th>Text</th>
            <th>Audio</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {LANG_LIST.map(([code, label]) => {
            const t = translations[code] || 0;
            const a = audio[code] || 0;
            const cap = 56;
            const state = a >= cap ? "done"
              : a > 0 ? "in-progress"
              : t > 0 ? "queued"
              : "no-text";
            return (
              <tr key={code} data-testid={`worker-row-${code}`}>
                <td>{label}</td>
                <td>{t}/{cap}</td>
                <td>{a}/{cap}</td>
                <td>
                  <span className={`worker-pill worker-pill-${state}`}>
                    {state === "done" && "✓ done"}
                    {state === "in-progress" && (code === status.current_lang ? "▶ generating" : "in progress")}
                    {state === "queued" && "queued"}
                    {state === "no-text" && "pending translation"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Heartbeat detail */}
      {status.last_heartbeat && (
        <details className="worker-heartbeat">
          <summary>Last heartbeat ({status.last_heartbeat.timestamp || "—"})</summary>
          <pre>{JSON.stringify(status.last_heartbeat, null, 2)}</pre>
        </details>
      )}

      <p className="admin-muted worker-help">
        The worker auto-starts on the deployed production container and runs
        24/7. It survives logouts and browser closes — once started it keeps
        producing audio until all 12 languages are complete. To opt out (e.g.
        in a preview container running supervisor) set the env var{" "}
        <code>AUDIO_WORKER_DISABLED=1</code>.
      </p>
    </div>
  );
}
